/**
 * Painel de Anotações do Usuário
 * Permite criar, editar e gerenciar anotações, destaques e favoritos em segmentos
 */

import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Alert,
  Tooltip,
  Divider,
  Badge,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import HighlightIcon from '@mui/icons-material/Highlight'
import NoteIcon from '@mui/icons-material/Note'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import LinkIcon from '@mui/icons-material/Link'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { 
  Annotation, 
  SegmentHighlight, 
  Bookmark, 
  UserAnnotations,
  AnnotationColor,
  AnnotationCategory,
  Transcription
} from '../../types/transcription'
import { getAnnotations, updateAnnotations } from '../../services/api'

type Props = {
  fileId: string
  transcription?: Transcription
  onNavigateToSegment?: (segmentIndex: number) => void
  // Props para estado compartilhado (opcional)
  annotations?: UserAnnotations
  onAnnotationsChange?: (annotations: UserAnnotations) => void
}

type SubTabValue = 'annotations' | 'highlights' | 'bookmarks'

const COLORS: { value: AnnotationColor; label: string; hex: string }[] = [
  { value: 'yellow', label: 'Amarelo', hex: '#fff59d' },
  { value: 'green', label: 'Verde', hex: '#a5d6a7' },
  { value: 'blue', label: 'Azul', hex: '#90caf9' },
  { value: 'red', label: 'Vermelho', hex: '#ef9a9a' },
  { value: 'purple', label: 'Roxo', hex: '#ce93d8' }
]

const CATEGORIES: { value: AnnotationCategory; label: string; icon: React.ReactNode }[] = [
  { value: 'note', label: 'Nota', icon: <NoteIcon fontSize="small" /> },
  { value: 'question', label: 'Dúvida', icon: <QuestionMarkIcon fontSize="small" /> },
  { value: 'important', label: 'Importante', icon: <PriorityHighIcon fontSize="small" /> },
  { value: 'todo', label: 'A Fazer', icon: <CheckCircleOutlineIcon fontSize="small" /> },
  { value: 'reference', label: 'Referência', icon: <LinkIcon fontSize="small" /> }
]

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

function formatTime(timeStr: string | number | undefined | null): string {
  if (timeStr === null || timeStr === undefined) return '00:00'
  
  // Se for número, converte para string no formato MM:SS
  if (typeof timeStr === 'number') {
    const totalSeconds = Math.floor(timeStr)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }
  
  // Se não for string, tenta converter
  const str = String(timeStr)
  if (!str) return '00:00'
  
  const parts = str.split(':')
  if (parts.length === 3) {
    return `${parts[0]}:${parts[1]}:${parts[2].split('.')[0]}`
  }
  if (parts.length === 2) {
    return `${parts[0]}:${parts[1].split('.')[0]}`
  }
  const totalSeconds = Math.floor(parseFloat(str))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export default function AnnotationsPanel({ 
  fileId, 
  transcription, 
  onNavigateToSegment,
  annotations: externalAnnotations,
  onAnnotationsChange 
}: Props) {
  const [subTab, setSubTab] = useState<SubTabValue>('annotations')
  const [internalData, setInternalData] = useState<UserAnnotations>({ items: [], highlights: [], bookmarks: [] })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Usar dados externos se disponíveis, caso contrário usar internos
  const isExternallyControlled = externalAnnotations !== undefined && onAnnotationsChange !== undefined
  const data = isExternallyControlled ? externalAnnotations : internalData
  
  // Modal states
  const [annotationDialogOpen, setAnnotationDialogOpen] = useState(false)
  const [highlightDialogOpen, setHighlightDialogOpen] = useState(false)
  const [bookmarkDialogOpen, setBookmarkDialogOpen] = useState(false)
  
  // Edit item state
  const [editingAnnotation, setEditingAnnotation] = useState<Annotation | null>(null)
  const [editingHighlight, setEditingHighlight] = useState<SegmentHighlight | null>(null)
  const [editingBookmark, setEditingBookmark] = useState<Bookmark | null>(null)
  
  // Form states
  const [formSegmentIndex, setFormSegmentIndex] = useState<number>(0)
  const [formText, setFormText] = useState('')
  const [formColor, setFormColor] = useState<AnnotationColor>('yellow')
  const [formCategory, setFormCategory] = useState<AnnotationCategory>('note')
  const [formLabel, setFormLabel] = useState('')
  const [formTitle, setFormTitle] = useState('')
  const [formDescription, setFormDescription] = useState('')

  const segments = transcription?.transcript || []

  // Load annotations on mount (only if not externally controlled)
  useEffect(() => {
    if (!isExternallyControlled) {
      loadAnnotations()
    } else {
      setLoading(false)
    }
  }, [fileId, isExternallyControlled])

  const loadAnnotations = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await getAnnotations(fileId)
      setInternalData(result)
    } catch (e) {
      console.error('Erro ao carregar anotações:', e)
      setError('Não foi possível carregar as anotações.')
    } finally {
      setLoading(false)
    }
  }

  const saveAnnotations = async (newData: UserAnnotations) => {
    // Se controlado externamente, apenas notifica a mudança
    if (isExternallyControlled) {
      onAnnotationsChange(newData)
      return
    }
    
    // Caso contrário, salva diretamente
    setSaving(true)
    try {
      await updateAnnotations(fileId, newData)
      setInternalData(newData)
    } catch (e) {
      console.error('Erro ao salvar anotações:', e)
      setError('Erro ao salvar. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  // Reset forms
  const resetAnnotationForm = () => {
    setFormSegmentIndex(0)
    setFormText('')
    setFormColor('yellow')
    setFormCategory('note')
    setEditingAnnotation(null)
  }

  const resetHighlightForm = () => {
    setFormSegmentIndex(0)
    setFormColor('yellow')
    setFormLabel('')
    setEditingHighlight(null)
  }

  const resetBookmarkForm = () => {
    setFormSegmentIndex(0)
    setFormTitle('')
    setFormDescription('')
    setEditingBookmark(null)
  }

  // Annotation handlers
  const handleOpenAnnotationDialog = (annotation?: Annotation) => {
    if (annotation) {
      setEditingAnnotation(annotation)
      setFormSegmentIndex(annotation.segmentIndex)
      setFormText(annotation.text)
      setFormColor(annotation.color)
      setFormCategory(annotation.category)
    } else {
      resetAnnotationForm()
    }
    setAnnotationDialogOpen(true)
  }

  const handleSaveAnnotation = async () => {
    const segment = segments[formSegmentIndex]
    const now = Date.now()
    
    if (editingAnnotation) {
      const updated: Annotation = {
        ...editingAnnotation,
        segmentIndex: formSegmentIndex,
        startTime: segment?.start_time || '00:00',
        text: formText,
        segmentText: segment?.text?.substring(0, 100) || '',
        color: formColor,
        category: formCategory,
        updatedAt: now
      }
      const newItems = data.items.map(a => a.id === updated.id ? updated : a)
      await saveAnnotations({ ...data, items: newItems })
    } else {
      const newAnnotation: Annotation = {
        id: generateId(),
        segmentIndex: formSegmentIndex,
        startTime: segment?.start_time || '00:00',
        text: formText,
        segmentText: segment?.text?.substring(0, 100) || '',
        color: formColor,
        category: formCategory,
        createdAt: now,
        updatedAt: now
      }
      await saveAnnotations({ ...data, items: [...data.items, newAnnotation] })
    }
    
    setAnnotationDialogOpen(false)
    resetAnnotationForm()
  }

  const handleDeleteAnnotation = async (id: string) => {
    const newItems = data.items.filter(a => a.id !== id)
    await saveAnnotations({ ...data, items: newItems })
  }

  // Highlight handlers
  const handleOpenHighlightDialog = (highlight?: SegmentHighlight) => {
    if (highlight) {
      setEditingHighlight(highlight)
      setFormSegmentIndex(highlight.segmentIndex)
      setFormColor(highlight.color)
      setFormLabel(highlight.label || '')
    } else {
      resetHighlightForm()
    }
    setHighlightDialogOpen(true)
  }

  const handleSaveHighlight = async () => {
    const segment = segments[formSegmentIndex]
    const now = Date.now()
    
    if (editingHighlight) {
      const updated: SegmentHighlight = {
        ...editingHighlight,
        segmentIndex: formSegmentIndex,
        startTime: segment?.start_time || '00:00',
        color: formColor,
        label: formLabel || undefined,
        createdAt: editingHighlight.createdAt
      }
      const newHighlights = data.highlights.map(h => h.id === updated.id ? updated : h)
      await saveAnnotations({ ...data, highlights: newHighlights })
    } else {
      const newHighlight: SegmentHighlight = {
        id: generateId(),
        segmentIndex: formSegmentIndex,
        startTime: segment?.start_time || '00:00',
        color: formColor,
        label: formLabel || undefined,
        createdAt: now
      }
      await saveAnnotations({ ...data, highlights: [...data.highlights, newHighlight] })
    }
    
    setHighlightDialogOpen(false)
    resetHighlightForm()
  }

  const handleDeleteHighlight = async (id: string) => {
    const newHighlights = data.highlights.filter(h => h.id !== id)
    await saveAnnotations({ ...data, highlights: newHighlights })
  }

  // Bookmark handlers
  const handleOpenBookmarkDialog = (bookmark?: Bookmark) => {
    if (bookmark) {
      setEditingBookmark(bookmark)
      setFormSegmentIndex(bookmark.segmentIndex)
      setFormTitle(bookmark.title)
      setFormDescription(bookmark.description || '')
    } else {
      resetBookmarkForm()
    }
    setBookmarkDialogOpen(true)
  }

  const handleSaveBookmark = async () => {
    const segment = segments[formSegmentIndex]
    const now = Date.now()
    
    if (editingBookmark) {
      const updated: Bookmark = {
        ...editingBookmark,
        segmentIndex: formSegmentIndex,
        startTime: segment?.start_time || '00:00',
        title: formTitle,
        description: formDescription || undefined
      }
      const newBookmarks = data.bookmarks.map(b => b.id === updated.id ? updated : b)
      await saveAnnotations({ ...data, bookmarks: newBookmarks })
    } else {
      const newBookmark: Bookmark = {
        id: generateId(),
        segmentIndex: formSegmentIndex,
        startTime: segment?.start_time || '00:00',
        title: formTitle,
        description: formDescription || undefined,
        createdAt: now
      }
      await saveAnnotations({ ...data, bookmarks: [...data.bookmarks, newBookmark] })
    }
    
    setBookmarkDialogOpen(false)
    resetBookmarkForm()
  }

  const handleDeleteBookmark = async (id: string) => {
    const newBookmarks = data.bookmarks.filter(b => b.id !== id)
    await saveAnnotations({ ...data, bookmarks: newBookmarks })
  }

  // Navigation
  const handleNavigate = (segmentIndex: number) => {
    if (onNavigateToSegment) {
      onNavigateToSegment(segmentIndex)
    }
  }

  const getCategoryIcon = (category: AnnotationCategory) => {
    return CATEGORIES.find(c => c.value === category)?.icon || <NoteIcon fontSize="small" />
  }

  const getColorHex = (color: AnnotationColor) => {
    return COLORS.find(c => c.value === color)?.hex || '#fff59d'
  }

  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
        <Skeleton variant="text" width="40%" height={32} />
        <Box sx={{ mt: 2 }}>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} variant="rounded" height={60} sx={{ mb: 1 }} />
          ))}
        </Box>
      </Paper>
    )
  }

  return (
    <Paper elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          📝 Anotações
          {saving && <Typography variant="caption" color="text.secondary">(salvando...)</Typography>}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Crie notas, destaques e favoritos para organizar sua análise
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Sub-tabs */}
      <Tabs
        value={subTab}
        onChange={(_, v) => setSubTab(v)}
        sx={{ px: 2, borderBottom: '1px solid #e0e0e0' }}
      >
        <Tab 
          value="annotations" 
          label={
            <Badge badgeContent={data.items.length} color="primary" max={99}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <NoteIcon fontSize="small" /> Notas
              </Box>
            </Badge>
          } 
        />
        <Tab 
          value="highlights" 
          label={
            <Badge badgeContent={data.highlights.length} color="secondary" max={99}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <HighlightIcon fontSize="small" /> Destaques
              </Box>
            </Badge>
          } 
        />
        <Tab 
          value="bookmarks" 
          label={
            <Badge badgeContent={data.bookmarks.length} color="info" max={99}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BookmarkIcon fontSize="small" /> Favoritos
              </Box>
            </Badge>
          } 
        />
      </Tabs>

      <Box sx={{ p: 2 }}>
        {/* Annotations Tab */}
        {subTab === 'annotations' && (
          <>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenAnnotationDialog()}
              sx={{ mb: 2 }}
              disabled={segments.length === 0}
            >
              Nova Nota
            </Button>

            {data.items.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <NoteIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                <Typography>Nenhuma nota criada</Typography>
                <Typography variant="body2">Clique em "Nova Nota" para começar</Typography>
              </Box>
            ) : (
              <List>
                {data.items.sort((a, b) => a.segmentIndex - b.segmentIndex).map((annotation) => (
                  <ListItem
                    key={annotation.id}
                    sx={{
                      mb: 1,
                      bgcolor: getColorHex(annotation.color),
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'rgba(0,0,0,0.1)'
                    }}
                  >
                    <ListItemText
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ component: 'div' }}
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          {getCategoryIcon(annotation.category)}
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {annotation.text}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            📍 Segmento {annotation.segmentIndex + 1} • {formatTime(annotation.startTime)}
                          </Typography>
                          <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                            "{annotation.segmentText}..."
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Criado em {formatDate(annotation.createdAt)}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Ir para segmento">
                        <IconButton size="small" onClick={() => handleNavigate(annotation.segmentIndex)}>
                          <NavigateNextIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleOpenAnnotationDialog(annotation)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => handleDeleteAnnotation(annotation.id)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}

        {/* Highlights Tab */}
        {subTab === 'highlights' && (
          <>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenHighlightDialog()}
              sx={{ mb: 2 }}
              disabled={segments.length === 0}
            >
              Novo Destaque
            </Button>

            {data.highlights.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <HighlightIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                <Typography>Nenhum destaque criado</Typography>
                <Typography variant="body2">Destaque segmentos importantes</Typography>
              </Box>
            ) : (
              <List>
                {data.highlights.sort((a, b) => a.segmentIndex - b.segmentIndex).map((highlight) => (
                  <ListItem
                    key={highlight.id}
                    sx={{
                      mb: 1,
                      bgcolor: getColorHex(highlight.color),
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'rgba(0,0,0,0.1)'
                    }}
                  >
                    <ListItemText
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ component: 'div' }}
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <HighlightIcon fontSize="small" />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Segmento {highlight.segmentIndex + 1}
                            {highlight.label && ` - ${highlight.label}`}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            ⏱️ {formatTime(highlight.startTime)} • Criado em {formatDate(highlight.createdAt)}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Ir para segmento">
                        <IconButton size="small" onClick={() => handleNavigate(highlight.segmentIndex)}>
                          <NavigateNextIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleOpenHighlightDialog(highlight)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => handleDeleteHighlight(highlight.id)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}

        {/* Bookmarks Tab */}
        {subTab === 'bookmarks' && (
          <>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenBookmarkDialog()}
              sx={{ mb: 2 }}
              disabled={segments.length === 0}
            >
              Novo Favorito
            </Button>

            {data.bookmarks.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <BookmarkIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                <Typography>Nenhum favorito criado</Typography>
                <Typography variant="body2">Marque momentos importantes para acessar rapidamente</Typography>
              </Box>
            ) : (
              <List>
                {data.bookmarks.sort((a, b) => a.segmentIndex - b.segmentIndex).map((bookmark) => (
                  <ListItem
                    key={bookmark.id}
                    sx={{
                      mb: 1,
                      bgcolor: '#f5f5f5',
                      borderRadius: 1,
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    <ListItemText
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ component: 'div' }}
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BookmarkIcon fontSize="small" color="primary" />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {bookmark.title}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            📍 Segmento {bookmark.segmentIndex + 1} • {formatTime(bookmark.startTime)}
                          </Typography>
                          {bookmark.description && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                              {bookmark.description}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            Criado em {formatDate(bookmark.createdAt)}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Ir para segmento">
                        <IconButton size="small" onClick={() => handleNavigate(bookmark.segmentIndex)}>
                          <NavigateNextIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleOpenBookmarkDialog(bookmark)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => handleDeleteBookmark(bookmark.id)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
      </Box>

      {/* Annotation Dialog */}
      <Dialog open={annotationDialogOpen} onClose={() => setAnnotationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAnnotation ? 'Editar Nota' : 'Nova Nota'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Segmento</InputLabel>
              <Select
                value={formSegmentIndex}
                onChange={(e) => setFormSegmentIndex(Number(e.target.value))}
                label="Segmento"
              >
                {segments.map((seg, idx) => (
                  <MenuItem key={idx} value={idx}>
                    {idx + 1}. [{formatTime(seg.start_time)}] {seg.text?.substring(0, 50)}...
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Texto da nota"
              multiline
              rows={3}
              value={formText}
              onChange={(e) => setFormText(e.target.value)}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Categoria</InputLabel>
              <Select
                value={formCategory}
                onChange={(e) => setFormCategory(e.target.value as AnnotationCategory)}
                label="Categoria"
              >
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {cat.icon} {cat.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Cor</Typography>
              <ToggleButtonGroup
                value={formColor}
                exclusive
                onChange={(_, val) => val && setFormColor(val)}
                size="small"
              >
                {COLORS.map((color) => (
                  <ToggleButton key={color.value} value={color.value}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        bgcolor: color.hex,
                        borderRadius: '50%',
                        border: '1px solid rgba(0,0,0,0.2)'
                      }}
                    />
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnnotationDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveAnnotation} disabled={!formText.trim()}>
            {editingAnnotation ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Highlight Dialog */}
      <Dialog open={highlightDialogOpen} onClose={() => setHighlightDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingHighlight ? 'Editar Destaque' : 'Novo Destaque'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Segmento</InputLabel>
              <Select
                value={formSegmentIndex}
                onChange={(e) => setFormSegmentIndex(Number(e.target.value))}
                label="Segmento"
              >
                {segments.map((seg, idx) => (
                  <MenuItem key={idx} value={idx}>
                    {idx + 1}. [{formatTime(seg.start_time)}] {seg.text?.substring(0, 50)}...
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Rótulo (opcional)"
              value={formLabel}
              onChange={(e) => setFormLabel(e.target.value)}
              fullWidth
              placeholder="Ex: Trecho importante"
            />

            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Cor do destaque</Typography>
              <ToggleButtonGroup
                value={formColor}
                exclusive
                onChange={(_, val) => val && setFormColor(val)}
                size="small"
              >
                {COLORS.map((color) => (
                  <ToggleButton key={color.value} value={color.value}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        bgcolor: color.hex,
                        borderRadius: '50%',
                        border: '1px solid rgba(0,0,0,0.2)'
                      }}
                    />
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHighlightDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveHighlight}>
            {editingHighlight ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bookmark Dialog */}
      <Dialog open={bookmarkDialogOpen} onClose={() => setBookmarkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingBookmark ? 'Editar Favorito' : 'Novo Favorito'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Segmento</InputLabel>
              <Select
                value={formSegmentIndex}
                onChange={(e) => setFormSegmentIndex(Number(e.target.value))}
                label="Segmento"
              >
                {segments.map((seg, idx) => (
                  <MenuItem key={idx} value={idx}>
                    {idx + 1}. [{formatTime(seg.start_time)}] {seg.text?.substring(0, 50)}...
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Título"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              fullWidth
              placeholder="Ex: Momento crucial da entrevista"
            />

            <TextField
              label="Descrição (opcional)"
              multiline
              rows={2}
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookmarkDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveBookmark} disabled={!formTitle.trim()}>
            {editingBookmark ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}
